import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spin, Modal, Form, Input, Select, InputNumber, message } from 'antd';
import {
  FilterOutlined, SearchOutlined, CloseOutlined,
  LaptopOutlined, PrinterOutlined, ToolOutlined, CoffeeOutlined,
  CarOutlined, ExperimentOutlined, AppstoreOutlined, EnvironmentOutlined, LeftOutlined,
} from '@ant-design/icons';
import api from '../api';
import { getStoredUser, hasPerm, isSuperAdmin as checkSuperAdmin } from '../userAuth';
import { saveAssetListReturnQs, assetListPathFromQs, savePreFilterQs, getPreFilterQs, setAdvancedFilterActive, isAdvancedFilterActive } from '../utils/assetListNav';
import { H5_TAB_BAR_PX } from '../h5Layout';

const TYPE_STYLES = {
  '电子设备': { color: '#1677FF', bg: '#E8F0FE', icon: <LaptopOutlined /> },
  '办公设备': { color: '#52c41a', bg: '#E8F8E0', icon: <PrinterOutlined /> },
  '办公家具': { color: '#fa8c16', bg: '#FFF3E0', icon: <CoffeeOutlined /> },
  '交通工具': { color: '#722ed1', bg: '#F3E8FF', icon: <CarOutlined /> },
  '实验设备': { color: '#eb2f96', bg: '#FFE8F0', icon: <ExperimentOutlined /> },
  '维修工具': { color: '#13c2c2', bg: '#E0F8F8', icon: <ToolOutlined /> },
};
const DEFAULT_TYPE = { color: '#8c8c8c', bg: '#f0f0f0', icon: <AppstoreOutlined /> };
const SPECIAL_LAST_LOCATION_NAME = '3办公楼库房大连港专门办案场所边检处606室办公室6楼小木屋';

function getTypeStyle(type) {
  return TYPE_STYLES[type] || DEFAULT_TYPE;
}

const PARAM_KEYS = [
  'keyword', 'partition_id', 'location_id', 'user_id', 'asset_status',
  'asset_name', 'asset_code', 'check_code',
  'net_value_min', 'net_value_max', 'remaining_months_min', 'remaining_months_max',
  'managed', 'disposed_only', 'disposable_only', 'scrap_apply_only',
];

/** 真正的高级查询字段（不含分区/侧栏房间） */
const ADVANCED_FILTER_KEYS = [
  'asset_name', 'asset_code', 'check_code',
  'asset_status',
  'net_value_min', 'net_value_max',
  'remaining_months_min', 'remaining_months_max',
];

function parseParams(searchParams) {
  const o = {};
  PARAM_KEYS.forEach((k) => {
    const v = searchParams.get(k);
    if (v !== null && v !== '') o[k] = v;
  });
  return o;
}

function formatAssetCodeDisplay(code, maxLen = 12, tailLen = 8) {
  const s = String(code || '').trim();
  if (!s) return '-';
  if (s.length <= maxLen) return s;
  return `…${s.slice(-tailLen)}`;
}

function compareLocationNameAsc(a, b) {
  const left = String(a || '').trim();
  const right = String(b || '').trim();
  const leftSpecial = left === SPECIAL_LAST_LOCATION_NAME;
  const rightSpecial = right === SPECIAL_LAST_LOCATION_NAME;
  if (leftSpecial && !rightSpecial) return 1;
  if (!leftSpecial && rightSpecial) return -1;
  const leftParts = left.split(/(\d+)/).filter(Boolean);
  const rightParts = right.split(/(\d+)/).filter(Boolean);
  const len = Math.max(leftParts.length, rightParts.length);
  for (let i = 0; i < len; i += 1) {
    const lp = leftParts[i];
    const rp = rightParts[i];
    if (lp === undefined) return -1;
    if (rp === undefined) return 1;
    const lNum = /^\d+$/.test(lp);
    const rNum = /^\d+$/.test(rp);
    if (lNum && rNum) {
      const lText = String(lp);
      const rText = String(rp);
      // 先按“数字首位”分组排序：1开头在3/4开头前（如 107 在 3楼/4楼前）
      const lHead = Number(lText[0]);
      const rHead = Number(rText[0]);
      if (!Number.isNaN(lHead) && !Number.isNaN(rHead) && lHead !== rHead) {
        return lHead - rHead;
      }
      const diff = Number(lText) - Number(rText);
      if (diff !== 0) return diff;
      continue;
    }
    if (lNum && !rNum) return -1;
    if (!lNum && rNum) return 1;
    const cmp = String(lp).localeCompare(String(rp), 'zh-Hans-CN', { sensitivity: 'base' });
    if (cmp !== 0) return cmp;
  }
  return 0;
}

export default function AssetList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlFilters = useMemo(() => parseParams(searchParams), [searchParams]);

  const [keyword, setKeyword] = useState(searchParams.get('keyword') || '');
  const [searchValue, setSearchValue] = useState(searchParams.get('keyword') || '');
  const [partitionId, setPartitionId] = useState(searchParams.get('partition_id') || '');
  const [selectedLocationId, setSelectedLocationId] = useState(searchParams.get('location_id') || '');
  const [partitions, setPartitions] = useState([]);
  const [partitionLocations, setPartitionLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [filteredLocationsLoading, setFilteredLocationsLoading] = useState(false);
  const [assets, setAssets] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentTotalQuantity, setCurrentTotalQuantity] = useState(0);
  const [contextTotalQuantity, setContextTotalQuantity] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchVisible, setSearchVisible] = useState(false);
  const [advancedFilterOn, setAdvancedFilterOn] = useState(() => isAdvancedFilterActive());
  const [form] = Form.useForm();
  const [locations, setLocations] = useState([]);
  const [assetUsers, setAssetUsers] = useState([]);
  const [filterOptsLoading, setFilterOptsLoading] = useState(false);
  const [locationDetail, setLocationDetail] = useState(null);
  const [roomTotalQuantity, setRoomTotalQuantity] = useState(0);
  const [roomTotalNetValue, setRoomTotalNetValue] = useState(0);
  const [currentTotalNetValue, setCurrentTotalNetValue] = useState(0);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedAssetIds, setSelectedAssetIds] = useState([]);
  const longPressTimerRef = useRef(null);
  const longPressTriggeredRef = useRef(false);
  const assetGridScrollRef = useRef(null);
  const locationListScrollRef = useRef(null);
  const pageSize = 20;
  const sessionUser = getStoredUser();
  const canApplyDispose = hasPerm(sessionUser, 'apply_scrap');
  const isSuperAdmin = checkSuperAdmin(sessionUser);
  const isMyAssetsMode = Boolean(urlFilters.user_id);
  const isManagedAssetsMode = String(urlFilters.managed || '') === '1';
  const isDisposedAssetsMode = String(urlFilters.disposed_only || '') === '1';
  const isDisposableAssetsMode = String(urlFilters.disposable_only || '') === '1';
  const isScrapApplyMode = String(urlFilters.scrap_apply_only || '') === '1';
  const supportsBatchApply = isScrapApplyMode && canApplyDispose && !isSuperAdmin;
  const supportsBatchDirect = isDisposableAssetsMode && isSuperAdmin;
  const supportsBatchWithdraw = isDisposedAssetsMode && canApplyDispose && !isSuperAdmin;
  const supportsBatchDeleteDisposed = isDisposedAssetsMode && isSuperAdmin;
  const supportsMultiDispose = supportsBatchApply || supportsBatchDirect || supportsBatchWithdraw || supportsBatchDeleteDisposed;
  const isMyAssetsFromProfile = isMyAssetsMode
    && sessionUser?.id != null
    && String(urlFilters.user_id) === String(sessionUser.id);
  const hasKeywordSearch = Boolean((searchParams.get('keyword') || '').trim());
  const hasFieldAdvancedFilters = useMemo(
    () => ADVANCED_FILTER_KEYS.some((k) => urlFilters[k] !== undefined && urlFilters[k] !== ''),
    [urlFilters]
  );
  /** 高级查询生效中（表单条件，或高级查询里选了位置/使用人） */
  const hasActiveAdvancedFilters = useMemo(() => {
    if (hasFieldAdvancedFilters) return true;
    if (!advancedFilterOn) return false;
    if (urlFilters.location_id) return true;
    if (urlFilters.user_id && !isMyAssetsMode) return true;
    return false;
  }, [hasFieldAdvancedFilters, advancedFilterOn, urlFilters, isMyAssetsMode]);
  const hasAdvancedFilters = hasActiveAdvancedFilters;
  const isSearchOrFilterJump = useMemo(
    () => PARAM_KEYS.some((k) => {
      const v = urlFilters[k];
      return v !== undefined && v !== '';
    }) || Boolean((searchParams.get('keyword') || '').trim()),
    [urlFilters, searchParams]
  );
  const jumpLabel = isMyAssetsMode
    ? '我的'
    : (isManagedAssetsMode ? '我的区域' : (isDisposableAssetsMode ? '处置资产' : (isDisposedAssetsMode ? '已处置资产' : (hasKeywordSearch ? '搜索' : (hasAdvancedFilters ? '筛选' : '筛选/搜索/我的')))));
  const hasSearchCriteria = useMemo(() => {
    const quick = Boolean((keyword || '').trim());
    const advanced = ADVANCED_FILTER_KEYS.some((k) => urlFilters[k] !== undefined && urlFilters[k] !== '');
    return quick || advanced;
  }, [keyword, urlFilters]);
  const useFilteredLocations = isMyAssetsMode || isManagedAssetsMode || hasSearchCriteria;

  /** 把分区/房间/搜索等写入 URL，便于进详情后再返回 */
  const commitListFilters = useCallback((overrides = {}) => {
    const nextKeyword = overrides.keyword !== undefined ? overrides.keyword : keyword;
    const nextPartition = overrides.partition_id !== undefined ? overrides.partition_id : partitionId;
    const nextLocation = overrides.location_id !== undefined ? overrides.location_id : selectedLocationId;
    const params = new URLSearchParams();

    const kw = String(nextKeyword || '').trim();
    if (kw) params.set('keyword', kw);
    if (nextPartition) params.set('partition_id', String(nextPartition));
    if (nextLocation) params.set('location_id', String(nextLocation));

    // 保留高级筛选与模式标记（未在 overrides 中清掉的）
    PARAM_KEYS.forEach((k) => {
      if (['keyword', 'partition_id', 'location_id'].includes(k)) return;
      if (Object.prototype.hasOwnProperty.call(overrides, k)) {
        const v = overrides[k];
        if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
        return;
      }
      if (urlFilters[k] !== undefined && urlFilters[k] !== '') params.set(k, String(urlFilters[k]));
    });

    if (isManagedAssetsMode) params.set('managed', '1');
    if (isDisposedAssetsMode) params.set('disposed_only', '1');
    if (isDisposableAssetsMode) params.set('disposable_only', '1');
    if (isScrapApplyMode) params.set('scrap_apply_only', '1');

    const qs = params.toString();
    saveAssetListReturnQs(qs);
    const path = qs ? `/assets?${qs}` : '/assets';
    const cur = `${window.location.pathname}${window.location.search}`;
    if (cur !== path) navigate(path, { replace: true });
  }, [
    navigate, keyword, partitionId, selectedLocationId, urlFilters,
    isManagedAssetsMode, isDisposedAssetsMode, isDisposableAssetsMode, isScrapApplyMode,
  ]);

  const clearSearchAndFilters = useCallback(() => {
    commitListFilters({
      keyword: '',
      location_id: '',
      asset_name: '',
      asset_code: '',
      check_code: '',
      asset_status: '',
      net_value_min: '',
      net_value_max: '',
      remaining_months_min: '',
      remaining_months_max: '',
      // 保留分区与模式
      partition_id: partitionId,
    });
  }, [commitListFilters, partitionId]);

  /** 清空高级查询，回到开启筛选前的列表状态 */
  const clearAdvancedFilters = useCallback(() => {
    const prevQs = getPreFilterQs();
    setAdvancedFilterActive(false);
    setAdvancedFilterOn(false);
    savePreFilterQs('');
    form.resetFields();
    setSearchValue('');
    setKeyword('');
    const path = assetListPathFromQs(prevQs);
    saveAssetListReturnQs(prevQs);
    navigate(path, { replace: true });
  }, [form, navigate]);

  useEffect(() => {
    api.get('/partitions').then(res => setPartitions(Array.isArray(res) ? res : [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!searchVisible) return undefined;
    let cancelled = false;
    setFilterOptsLoading(true);
    api
      .get('/assets/filter-options')
      .then((data) => {
        if (cancelled) return;
        setLocations(Array.isArray(data?.locations) ? data.locations : []);
        setAssetUsers(Array.isArray(data?.asset_users) ? data.asset_users : []);
      })
      .catch((err) => {
        if (!cancelled) {
          message.error(err.message || '加载筛选项失败，请稍后重试');
          setLocations([]);
          setAssetUsers([]);
        }
      })
      .finally(() => {
        if (!cancelled) setFilterOptsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [searchVisible]);

  useEffect(() => {
    if (!searchVisible) return;
    const num = (v) => (v !== undefined && v !== '' ? Number(v) : undefined);
    // 侧栏当前房间 ≠ 高级查询条件；仅在高级筛选生效时回填位置/使用人
    const fillAdvLocation = hasActiveAdvancedFilters && advancedFilterOn;
    form.resetFields();
    form.setFieldsValue({
      name: urlFilters.asset_name || undefined,
      asset_no: urlFilters.asset_code || undefined,
      check_no: urlFilters.check_code || undefined,
      location_id: fillAdvLocation ? num(urlFilters.location_id) : undefined,
      user_id: fillAdvLocation && !isMyAssetsFromProfile
        ? num(urlFilters.user_id)
        : undefined,
      asset_status: urlFilters.asset_status !== undefined && urlFilters.asset_status !== ''
        ? Number(urlFilters.asset_status)
        : undefined,
      net_value_min: num(urlFilters.net_value_min),
      net_value_max: num(urlFilters.net_value_max),
      remaining_months_min: num(urlFilters.remaining_months_min),
      remaining_months_max: num(urlFilters.remaining_months_max),
    });
  }, [searchVisible, urlFilters, form, hasActiveAdvancedFilters, advancedFilterOn, isMyAssetsFromProfile]);

  useEffect(() => {
    const kw = searchParams.get('keyword') || '';
    setKeyword(kw);
    setSearchValue(kw);
    setPartitionId(searchParams.get('partition_id') || '');
    setSelectedLocationId(searchParams.get('location_id') || '');
    setPage(1);
    saveAssetListReturnQs(searchParams.toString());
  }, [searchParams]);

  useEffect(() => {
    setSelectionMode(false);
    setSelectedAssetIds([]);
  }, [searchParams, page, selectedLocationId, partitionId]);

  useEffect(() => () => {
    clearLongPressTimer();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const params = { sort_by: 'location_name:asc', status: 1 };
    if (partitionId) params.partition_ids = partitionId;
    api.get('/asset-locations', { params })
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res?.items) ? res.items : (Array.isArray(res) ? res : []);
        const sorted = [...list].sort((a, b) => compareLocationNameAsc(a.location_name, b.location_name));
        setPartitionLocations(sorted);
      })
      .catch(() => {
        if (cancelled) return;
        setPartitionLocations([]);
        setSelectedLocationId('');
      });
    return () => {
      cancelled = true;
    };
  }, [partitionId]);

  useEffect(() => {
    let cancelled = false;
    if (!useFilteredLocations) {
      setFilteredLocations([]);
      setFilteredLocationsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const loadFilteredLocations = async () => {
      setFilteredLocationsLoading(true);
      const summaryMap = new Map();
      const summaryParams = {};
      PARAM_KEYS.forEach((k) => {
        if (k === 'location_id') return;
        const v = urlFilters[k];
        if (v !== undefined && v !== '') {
          if (['location_id', 'user_id', 'asset_status', 'net_value_min', 'net_value_max',
            'remaining_months_min', 'remaining_months_max'].includes(k)) {
            const n = Number(v);
            if (!Number.isNaN(n)) summaryParams[k] = n;
          } else {
            summaryParams[k] = v;
          }
        }
      });
      if (keyword) summaryParams.keyword = keyword;
      if (partitionId) summaryParams.partition_id = Number(partitionId);

      // 后端 page_size 上限为 100；超限会 422，左侧就会一直「无位置」
      const summaryPageSize = 100;
      const maxPages = 30;
      let current = 1;
      let totalCount = 0;
      let loaded = 0;
      try {
        // eslint-disable-next-line no-constant-condition
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const res = await api.get('/assets', {
            params: { ...summaryParams, page: current, page_size: summaryPageSize },
          });
          const rows = Array.isArray(res?.items) ? res.items : [];
          totalCount = Number(res?.total || 0);
          rows.forEach((item) => {
            const id = Number(item?.location_id);
            if (!Number.isNaN(id) && id > 0 && !summaryMap.has(id)) {
              summaryMap.set(id, {
                id,
                location_name: item?.location_name || '',
              });
            }
          });
          loaded += rows.length;
          if (!rows.length || loaded >= totalCount || current >= maxPages) break;
          current += 1;
        }
      } catch {
        if (!cancelled) {
          setFilteredLocations([]);
          setFilteredLocationsLoading(false);
        }
        return;
      }
      if (cancelled) return;
      const list = Array.from(summaryMap.values())
        .sort((a, b) => compareLocationNameAsc(a.location_name, b.location_name));
      setFilteredLocations(list);
      setFilteredLocationsLoading(false);
    };

    loadFilteredLocations();
    return () => {
      cancelled = true;
    };
  }, [useFilteredLocations, urlFilters, keyword, partitionId]);

  const displayedLocations = useMemo(() => {
    if (!useFilteredLocations) return partitionLocations;
    if (filteredLocations.length > 0) return filteredLocations;
    // 汇总未完成时，先用当前页资产里的地点，避免左侧「无位置」
    if (assets.length > 0) {
      const map = new Map();
      assets.forEach((item) => {
        const id = Number(item?.location_id);
        if (!Number.isNaN(id) && id > 0 && !map.has(id)) {
          map.set(id, { id, location_name: item?.location_name || '' });
        }
      });
      const fromAssets = Array.from(map.values())
        .sort((a, b) => compareLocationNameAsc(a.location_name, b.location_name));
      if (fromAssets.length) return fromAssets;
    }
    if (filteredLocationsLoading) return partitionLocations;
    return filteredLocations;
  }, [useFilteredLocations, filteredLocations, filteredLocationsLoading, partitionLocations, assets]);

  useEffect(() => {
    const urlLoc = searchParams.get('location_id') || '';
    const kw = (searchParams.get('keyword') || '').trim();
    // 关键词搜索未点选侧栏地点时，不沿用旧房间（否则右侧仍显示原房间且列表为空）
    if (kw && !urlLoc) {
      setSelectedLocationId('');
      return;
    }
    if (urlLoc) {
      // 搜索命中地点列表已出来，但旧 location 不在其中 → 从 URL 清掉，展示全部命中
      if (kw && useFilteredLocations && filteredLocations.length > 0
        && !filteredLocations.some((item) => String(item.id) === String(urlLoc))) {
        const next = new URLSearchParams(searchParams);
        next.delete('location_id');
        const qs = next.toString();
        navigate(qs ? `/assets?${qs}` : '/assets', { replace: true });
        return;
      }
      setSelectedLocationId(String(urlLoc));
      return;
    }
    if (!displayedLocations.length) {
      setSelectedLocationId('');
      return;
    }
    setSelectedLocationId((prev) => {
      if (prev && displayedLocations.some((item) => String(item.id) === String(prev))) return String(prev);
      // 搜索模式下默认不限定地点，右侧展示全部命中资产
      if (useFilteredLocations) return '';
      return String(displayedLocations[0].id);
    });
  }, [displayedLocations, useFilteredLocations, searchParams, filteredLocations, navigate]);

  useEffect(() => {
    if (!selectedLocationId) {
      setLocationDetail(null);
      return undefined;
    }
    let cancelled = false;
    api
      .get(`/asset-locations/${selectedLocationId}`)
      .then((data) => {
        if (!cancelled) setLocationDetail(data || null);
      })
      .catch(() => {
        if (!cancelled) setLocationDetail(null);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedLocationId]);

  useEffect(() => {
    if (!selectedLocationId) {
      setRoomTotalQuantity(0);
      setRoomTotalNetValue(0);
      return undefined;
    }
    let cancelled = false;
    const params = { page: 1, page_size: 1, location_id: Number(selectedLocationId) };
    if (partitionId) params.partition_id = Number(partitionId);
    if (keyword) params.keyword = keyword;
    api
      .get('/assets', { params })
      .then((res) => {
        if (!cancelled) {
          setRoomTotalQuantity(res?.total_quantity ?? 0);
          setRoomTotalNetValue(Number(res?.total_net_value ?? 0));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRoomTotalQuantity(0);
          setRoomTotalNetValue(0);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedLocationId, partitionId, keyword]);

  const formatCurrency = useCallback((val) => {
    if (val == null || Number.isNaN(Number(val))) return '0';
    const n = Number(val);
    if (n >= 10000) return `¥${(n / 10000).toFixed(2)}万`;
    return `¥${n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }, []);

  const fetchAssets = useCallback(() => {
    setLoading(true);
    const params = { page, page_size: pageSize };
    PARAM_KEYS.forEach((k) => {
      if (k === 'location_id') return;
      const v = urlFilters[k];
      if (v !== undefined && v !== '') {
        if (['location_id', 'user_id', 'asset_status', 'net_value_min', 'net_value_max',
          'remaining_months_min', 'remaining_months_max'].includes(k)) {
          const n = Number(v);
          if (!Number.isNaN(n)) params[k] = n;
        } else {
          params[k] = v;
        }
      }
    });
    if (keyword) params.keyword = keyword;
    if (partitionId) params.partition_id = Number(partitionId);
    if (selectedLocationId) params.location_id = Number(selectedLocationId);
    // 与左侧地点栏同序：按存放地点名升序
    params.sort_by = 'location_name:asc';
    if (isManagedAssetsMode) {
      params.managed = 1;
    }
    if (isDisposedAssetsMode) {
      params.disposed_only = 1;
    }
    if (isDisposableAssetsMode) {
      params.disposable_only = 1;
    }

    const contextParams = { ...params };
    delete contextParams.location_id;

    Promise.all([
      api.get('/assets', { params }),
      api.get('/assets', { params: { ...contextParams, page: 1, page_size: 1 } }),
    ])
      .then(([currentRes, contextRes]) => {
        let items = Array.isArray(currentRes?.items) ? [...currentRes.items] : [];
        // 前端再按地点名排一次，保证与左侧一致（后端排序规则可能略有差异）
        items.sort((a, b) => {
          const locCmp = compareLocationNameAsc(a?.location_name, b?.location_name);
          if (locCmp !== 0) return locCmp;
          return String(a?.asset_code || '').localeCompare(String(b?.asset_code || ''), 'zh');
        });
        setAssets(items);
        setTotal(currentRes?.total || 0);
        setCurrentTotalQuantity(currentRes?.total_quantity ?? currentRes?.total ?? 0);
        setCurrentTotalNetValue(Number(currentRes?.total_net_value ?? 0));
        setContextTotalQuantity(contextRes?.total_quantity ?? contextRes?.total ?? 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, keyword, partitionId, selectedLocationId, urlFilters, isManagedAssetsMode, isDisposedAssetsMode, isDisposableAssetsMode]);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);

  // 切换房间 / 分区 / 搜索 / 翻页后，右侧列表滚回顶部（避免停在上一房间的底部）
  useEffect(() => {
    const el = assetGridScrollRef.current;
    if (el) el.scrollTop = 0;
  }, [selectedLocationId, partitionId, keyword, page, searchParams]);

  // 切换分区后，左侧地点列表也回到顶部
  useEffect(() => {
    const el = locationListScrollRef.current;
    if (el) el.scrollTop = 0;
  }, [partitionId, keyword]);

  const handleSearch = () => {
    const kw = (searchValue || '').trim();
    // 关键词搜索跨分区/房间：清空当前房间与分区，避免仍卡在旧房间导致「有总数却暂无数据」
    commitListFilters({
      keyword: kw,
      location_id: '',
      ...(kw ? { partition_id: '' } : {}),
    });
  };

  const advancedFilterSummary = useMemo(() => {
    if (!hasActiveAdvancedFilters) return '';
    const parts = [];
    if (urlFilters.asset_name) parts.push(`名称:${urlFilters.asset_name}`);
    if (urlFilters.asset_code) parts.push(`编号:${urlFilters.asset_code}`);
    if (urlFilters.check_code) parts.push(`清查:${urlFilters.check_code}`);
    if (urlFilters.location_id && advancedFilterOn) {
      const locName = locationDetail?.location_name
        || displayedLocations.find((l) => String(l.id) === String(urlFilters.location_id))?.location_name
        || locations.find((l) => String(l.id) === String(urlFilters.location_id))?.location_name
        || `位置#${urlFilters.location_id}`;
      parts.push(`位置:${locName}`);
    }
    if (urlFilters.user_id && advancedFilterOn && !isMyAssetsFromProfile) {
      const uname = assetUsers.find((u) => String(u.id) === String(urlFilters.user_id))?.username
        || `使用人#${urlFilters.user_id}`;
      parts.push(`使用人:${uname}`);
    }
    if (urlFilters.asset_status !== undefined && urlFilters.asset_status !== '') {
      parts.push(`状态:${String(urlFilters.asset_status) === '1' ? '已完成折旧' : '折旧中'}`);
    }
    if (urlFilters.net_value_min || urlFilters.net_value_max) {
      parts.push(`净值:${urlFilters.net_value_min || '*'}~${urlFilters.net_value_max || '*'}`);
    }
    if (urlFilters.remaining_months_min || urlFilters.remaining_months_max) {
      parts.push(`剩月:${urlFilters.remaining_months_min || '*'}~${urlFilters.remaining_months_max || '*'}`);
    }
    return parts.length ? parts.join(' · ') : '高级筛选中';
  }, [
    hasActiveAdvancedFilters, urlFilters, advancedFilterOn, locationDetail,
    displayedLocations, locations, assetUsers, isMyAssetsFromProfile,
  ]);

  const handleAdvancedSearch = () => {
    const values = form.getFieldsValue();
    const locId = values.location_id !== undefined && values.location_id !== null && values.location_id !== ''
      ? values.location_id
      : '';
    // 首次开启高级查询时记下当前列表，便于一键还原
    if (!isAdvancedFilterActive()) {
      savePreFilterQs(searchParams.toString());
    }
    setAdvancedFilterActive(true);
    setAdvancedFilterOn(true);
    // 指定存放位置时清空分区，避免「当前办二 + 408」交集为空/仍停在旧房间
    commitListFilters({
      keyword: (searchValue || keyword || '').trim(),
      asset_name: values.name || '',
      asset_code: values.asset_no || '',
      check_code: values.check_no || '',
      location_id: locId,
      ...(locId !== '' ? { partition_id: '' } : {}),
      user_id: values.user_id !== undefined && values.user_id !== null && values.user_id !== ''
        ? values.user_id
        : (isMyAssetsMode ? (urlFilters.user_id || '') : ''),
      asset_status: values.asset_status !== undefined && values.asset_status !== null && values.asset_status !== ''
        ? values.asset_status
        : '',
      net_value_min: values.net_value_min ?? '',
      net_value_max: values.net_value_max ?? '',
      remaining_months_min: values.remaining_months_min ?? '',
      remaining_months_max: values.remaining_months_max ?? '',
    });
    setSearchVisible(false);
  };

  const totalPages = Math.ceil(total / pageSize);

  const clearLongPressTimer = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const toggleAssetSelected = (assetId) => {
    setSelectedAssetIds((prev) => (
      prev.includes(assetId)
        ? prev.filter((id) => id !== assetId)
        : [...prev, assetId]
    ));
  };

  const startLongPress = (assetId) => {
    if (!supportsMultiDispose) return;
    clearLongPressTimer();
    longPressTriggeredRef.current = false;
    longPressTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      setSelectionMode(true);
      setSelectedAssetIds((prev) => (prev.includes(assetId) ? prev : [...prev, assetId]));
    }, 500);
  };

  const endLongPress = () => {
    clearLongPressTimer();
  };

  const handleBatchApplyDispose = async () => {
    if (!canApplyDispose) {
      message.error('无申请处置权限');
      return;
    }
    if (!selectedAssetIds.length) {
      message.warning('请先选择资产');
      return;
    }
    Modal.confirm({
      title: '确认批量申请处置',
      content: `将提交 ${selectedAssetIds.length} 条资产的处置申请，是否继续？`,
      okText: '确认提交',
      cancelText: '取消',
      onOk: async () => {
        try {
          await api.post('/applies', {
            apply_type: 3,
            asset_ids: selectedAssetIds,
            remark: '批量申请处置',
          });
          message.success(`已提交 ${selectedAssetIds.length} 条资产处置申请`);
          setSelectionMode(false);
          setSelectedAssetIds([]);
          fetchAssets();
        } catch (err) {
          message.error(err.message || '批量申请处置失败');
        }
      },
    });
  };

  const handleBatchDirectDispose = async () => {
    if (!isSuperAdmin) {
      message.error('仅超级管理员可批量提交未上报');
      return;
    }
    if (!selectedAssetIds.length) {
      message.warning('请先选择资产');
      return;
    }
    Modal.confirm({
      title: '确认批量提交未上报',
      content: `将把 ${selectedAssetIds.length} 条资产提交到处置「未上报」，是否继续？`,
      okText: '确认提交',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await api.post('/assets/dispose-batch', {
            asset_ids: selectedAssetIds,
            remark: '超级管理员批量提交未上报',
          });
          message.success(`已提交 ${selectedAssetIds.length} 条至未上报`);
          setSelectionMode(false);
          setSelectedAssetIds([]);
          fetchAssets();
        } catch (err) {
          message.error(err.message || '批量提交失败');
        }
      },
    });
  };

  const handleBatchWithdrawDispose = async () => {
    if (!supportsBatchWithdraw) {
      message.error('无提交撤回申请权限');
      return;
    }
    if (!selectedAssetIds.length) {
      message.warning('请先选择资产');
      return;
    }
    Modal.confirm({
      title: '确认批量申请撤回',
      content: `将提交 ${selectedAssetIds.length} 条已处置资产的撤回申请，是否继续？`,
      okText: '确认提交',
      cancelText: '取消',
      onOk: async () => {
        try {
          await api.post('/applies', {
            apply_type: 4,
            asset_ids: selectedAssetIds,
            remark: '批量申请撤回处置',
          });
          message.success(`已提交 ${selectedAssetIds.length} 条撤回申请`);
          setSelectionMode(false);
          setSelectedAssetIds([]);
          fetchAssets();
        } catch (err) {
          message.error(err.message || '批量申请撤回失败');
        }
      },
    });
  };

  const handleBatchDeleteDisposed = async () => {
    if (!supportsBatchDeleteDisposed) {
      message.error('仅超级管理员可删除已处置资产');
      return;
    }
    if (!selectedAssetIds.length) {
      message.warning('请先选择资产');
      return;
    }
    Modal.confirm({
      title: '确认删除',
      content: `将软删除 ${selectedAssetIds.length} 条资产（保留留痕）。处置已完成的资产会被跳过。`,
      okText: '确认删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const res = await api.post('/assets/delete-batch', {
            scope: 'ids',
            ids: selectedAssetIds,
          });
          const n = Number(res?.deleted_count || selectedAssetIds.length);
          message.success(`已删除 ${n} 条`);
          setSelectionMode(false);
          setSelectedAssetIds([]);
          fetchAssets();
        } catch (err) {
          message.error(err.message || '批量删除失败');
        }
      },
    });
  };

  return (
    <div style={{
      height: `calc(100dvh - ${H5_TAB_BAR_PX}px)`,
      maxHeight: `calc(100dvh - ${H5_TAB_BAR_PX}px)`,
      background: '#f5f7fa',
      width: '100%',
      maxWidth: '100%',
      margin: '0 auto',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      boxSizing: 'border-box',
    }}
    >
      <div style={{
        height: 44,
        background: 'linear-gradient(180deg, #1677FF 0%, #4096ff 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 12,
        color: '#fff',
        flexShrink: 0,
      }}>
        {isSearchOrFilterJump && (
          <LeftOutlined
            onClick={() => navigate(-1)}
            style={{ position: 'absolute', left: 14, fontSize: 16, color: '#fff', cursor: 'pointer' }}
          />
        )}
        <span style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>
          {isManagedAssetsMode ? '我的区域' : (isDisposableAssetsMode ? '处置资产' : (isDisposedAssetsMode ? '已处置资产' : (isScrapApplyMode ? '申请处置资产' : '资产')))}
        </span>
      </div>
      <div style={{
        background: '#fff', padding: '4px 0', borderBottom: '1px solid #f0f0f0',
        overflowX: 'auto', whiteSpace: 'nowrap', WebkitOverflowScrolling: 'touch',
        flexShrink: 0,
      }}>
        <div style={{ display: 'inline-flex', gap: 0, padding: '0 8px' }}>
          {[{ id: '', name: '全部分区' }, ...partitions].map(r => {
            const key = String(r.id);
            const active = partitionId === key;
            return (
              <div
                key={key || 'all'}
                onClick={() => commitListFilters({ partition_id: key, location_id: '' })}
                style={{
                padding: '5px 12px', borderRadius: 16, cursor: 'pointer', fontSize: 12, flexShrink: 0,
                background: active ? '#1677FF' : 'transparent',
                color: active ? '#fff' : '#666',
                fontWeight: active ? 500 : 400,
              }}>
                {r.name}
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ padding: '8px 10px 0', flexShrink: 0 }}>
        <div style={{
          display: 'flex', alignItems: 'center', background: '#fff',
          borderRadius: 8, padding: '0 8px', height: 36, border: hasActiveAdvancedFilters
            ? '1px solid #91caff'
            : '1px solid #e8e8e8',
          backgroundColor: hasActiveAdvancedFilters ? '#f0f7ff' : '#fff',
        }}>
          <SearchOutlined style={{ color: hasActiveAdvancedFilters ? '#1677FF' : '#bbb', fontSize: 16, marginRight: 8 }} />
          {hasActiveAdvancedFilters && !searchValue ? (
            <div
              onClick={() => setSearchVisible(true)}
              title={advancedFilterSummary}
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: 13,
                color: '#1677FF',
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                lineHeight: '36px',
              }}
            >
              {advancedFilterSummary}
            </div>
          ) : (
            <input
              value={searchValue}
              onChange={e => setSearchValue(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder={hasActiveAdvancedFilters ? advancedFilterSummary : '搜索名称/编号/房间/使用人'}
              style={{
                flex: 1, border: 'none', outline: 'none', fontSize: 13,
                background: 'transparent', color: '#333',
                minWidth: 0,
              }}
            />
          )}
          <div
            title="高级查询"
            onClick={() => setSearchVisible(true)}
            style={{
              width: 28,
              height: 28,
              borderRadius: 14,
              background: hasActiveAdvancedFilters ? '#1677FF' : '#E8F4FF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              flexShrink: 0,
              marginLeft: 6,
            }}
          >
            <FilterOutlined style={{ color: hasActiveAdvancedFilters ? '#fff' : '#1677FF', fontSize: 14 }} />
          </div>
          {(hasActiveAdvancedFilters || searchValue) && (
            <CloseOutlined
              style={{ color: '#999', fontSize: 13, cursor: 'pointer', marginLeft: 8, flexShrink: 0 }}
              title={hasActiveAdvancedFilters ? '清空筛选' : '清空搜索'}
              onClick={() => {
                if (hasActiveAdvancedFilters) clearAdvancedFilters();
                else clearSearchAndFilters();
              }}
            />
          )}
        </div>
      </div>

      <div style={{ padding: '6px 10px 2px', fontSize: 12, color: '#999', whiteSpace: 'nowrap', flexShrink: 0 }}>
        {isSearchOrFilterJump ? (
          <>
            {jumpLabel}共 <span style={{ color: '#333', fontWeight: 500 }}>{contextTotalQuantity}</span> 件资产
          </>
        ) : (
          <>
            共 <span style={{ color: '#333', fontWeight: 500 }}>{contextTotalQuantity}</span> 件资产
          </>
        )}
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, minHeight: 0, height: '100%', position: 'relative' }}>
          <div style={{
            padding: '0 8px 8px',
            display: 'flex',
            gap: 6,
            flex: 1,
            minHeight: 0,
            height: '100%',
            overflow: 'hidden',
          }}
          >
          <div
            ref={locationListScrollRef}
            className="asset-loc-sidebar"
            style={{
            width: 64,
            flexShrink: 0,
            background: '#fff',
            borderRadius: 8,
            border: '1px solid #f0f0f0',
            overflowY: 'auto',
            height: '100%',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
          }}>
            <style>{`
              @media (min-width: 768px) {
                .asset-loc-sidebar { width: 112px !important; }
                .asset-loc-name { font-size: 12px !important; }
              }
            `}</style>
            {displayedLocations.length === 0 ? (
              <div style={{ color: '#bbb', fontSize: 12, textAlign: 'center', padding: '16px 6px' }}>
                {filteredLocationsLoading ? '加载中' : '无位置'}
              </div>
            ) : (
              <>
                {useFilteredLocations ? (
                  <div
                    onClick={() => commitListFilters({ location_id: '' })}
                    style={{
                      padding: '6px 4px',
                      textAlign: 'center',
                      borderBottom: '1px solid #f5f5f5',
                      background: !selectedLocationId ? '#e6f4ff' : '#fff',
                      color: !selectedLocationId ? '#1677ff' : '#666',
                      fontWeight: !selectedLocationId ? 600 : 400,
                      cursor: 'pointer',
                      lineHeight: 1.3,
                      minHeight: 36,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 11,
                    }}
                  >
                    全部
                  </div>
                ) : null}
                {displayedLocations.map((loc) => {
              const active = String(loc.id) === String(selectedLocationId);
              const name = String(loc.location_name || '-');
              // 手机窄栏略缩小字号；电脑端由 .asset-loc-name 固定 12px，保证「办公楼201」一行
              const fontSize = name.length > 14 ? 9 : (name.length > 8 ? 10 : 11);
              return (
                <div
                  key={loc.id}
                  onClick={() => commitListFilters({ location_id: String(loc.id) })}
                  style={{
                    padding: '6px 4px',
                    textAlign: 'center',
                    borderBottom: '1px solid #f5f5f5',
                    background: active ? '#e6f4ff' : '#fff',
                    color: active ? '#1677ff' : '#666',
                    fontWeight: active ? 600 : 400,
                    cursor: 'pointer',
                    lineHeight: 1.3,
                    minHeight: 36,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <span
                    className="asset-loc-name"
                    style={{
                      fontSize,
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 2,
                      // 禁止数字/英文被拆开（避免「办公楼20」「1」）；过长名称仍可换到第二行
                      wordBreak: 'keep-all',
                      overflowWrap: 'break-word',
                      lineBreak: 'strict',
                    }}
                  >
                    {name}
                  </span>
                </div>
              );
            })}
              </>
            )}
          </div>

          <div style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            height: '100%',
            overflow: 'hidden',
          }}
          >
            {selectedLocationId ? (
              <div style={{
                flexShrink: 0,
                alignSelf: 'stretch',
                textAlign: 'left',
                fontSize: 11,
                color: '#434343',
                lineHeight: 1.4,
                padding: '6px 8px',
                marginBottom: 6,
                background: 'linear-gradient(135deg, #f0f7ff 0%, #e8f4ff 100%)',
                border: '1px solid #bae0ff',
                borderRadius: 8,
              }}
              >
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'baseline',
                  gap: '2px 10px',
                }}
                >
                  <span>
                    <span style={{ color: '#8c8c8c' }}>房间 </span>
                    <span style={{ fontWeight: 600, color: '#262626' }}>
                      {locationDetail?.location_name
                        || displayedLocations.find((l) => String(l.id) === String(selectedLocationId))?.location_name
                        || '-'}
                    </span>
                  </span>
                  {locationDetail?.password_visible ? (
                    <span>
                      <span style={{ color: '#8c8c8c' }}>密码 </span>
                      <span style={{ fontWeight: 500 }}>
                        {locationDetail?.password ? locationDetail.password : '无/机械钥匙'}
                      </span>
                    </span>
                  ) : null}
                  <span>
                    <span style={{ color: '#8c8c8c' }}>共 </span>
                    <span style={{ fontWeight: 600, color: '#1677FF' }}>{roomTotalQuantity}</span>
                    <span style={{ color: '#8c8c8c' }}> 件 · </span>
                    <span style={{ fontWeight: 600, color: '#1677FF' }}>{formatCurrency(roomTotalNetValue)}</span>
                  </span>
                  {isMyAssetsFromProfile ? (
                    <span>
                      <span style={{ color: '#8c8c8c' }}>我的 </span>
                      <span style={{ fontWeight: 600, color: '#389e0d' }}>{currentTotalQuantity}</span>
                      <span style={{ color: '#8c8c8c' }}> · </span>
                      <span style={{ fontWeight: 600, color: '#389e0d' }}>{formatCurrency(currentTotalNetValue)}</span>
                    </span>
                  ) : null}
                </div>
              </div>
            ) : null}
            <div
              ref={assetGridScrollRef}
              style={{
              flex: 1,
              minHeight: 0,
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gridAutoRows: 'max-content',
              gap: 8,
              overflowY: 'auto',
              overscrollBehavior: 'contain',
              WebkitOverflowScrolling: 'touch',
              alignContent: 'start',
              paddingBottom: 16,
            }}
            >
            {assets.length === 0 && !loading ? (
              <div style={{
                gridColumn: '1 / -1', textAlign: 'center', padding: '60px 0',
                color: '#bbb', fontSize: 14,
              }}>
                暂无数据
              </div>
            ) : (
              assets.map(a => {
                const ts = getTypeStyle(a.asset_type);
                const selected = selectedAssetIds.includes(a.id);
                return (
                  <div
                    key={a.id}
                    onClick={() => {
                      if (selectionMode) {
                        toggleAssetSelected(a.id);
                        return;
                      }
                      if (longPressTriggeredRef.current) {
                        longPressTriggeredRef.current = false;
                        return;
                      }
                      const listQs = searchParams.toString();
                      saveAssetListReturnQs(listQs);
                      const detailQs = new URLSearchParams();
                      if (isDisposedAssetsMode) detailQs.set('disposed_only', '1');
                      if (isDisposableAssetsMode) detailQs.set('disposable_only', '1');
                      if (isScrapApplyMode) detailQs.set('scrap_apply_only', '1');
                      const qs = detailQs.toString();
                      navigate(qs ? `/assets/${a.id}?${qs}` : `/assets/${a.id}`);
                    }}
                    onTouchStart={() => startLongPress(a.id)}
                    onTouchEnd={endLongPress}
                    onTouchCancel={endLongPress}
                    onMouseDown={() => startLongPress(a.id)}
                    onMouseUp={endLongPress}
                    onMouseLeave={endLongPress}
                    style={{
                    background: a.disposal_reported ? '#fff7e6' : '#fff',
                    borderRadius: 8, overflow: 'hidden',
                    cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    alignSelf: 'start',
                    position: 'relative',
                    border: selectionMode && selected
                      ? '2px solid #1677ff'
                      : (a.disposal_reported ? '1px solid #ffd591' : '1px solid #f0f0f0'),
                  }}>
                    {selectionMode && (
                      <div style={{
                        position: 'absolute',
                        right: 6,
                        top: 6,
                        width: 16,
                        height: 16,
                        borderRadius: 8,
                        border: `1px solid ${selected ? '#1677ff' : '#d9d9d9'}`,
                        background: selected ? '#1677ff' : '#fff',
                        color: '#fff',
                        fontSize: 11,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 3,
                      }}
                      >
                        {selected ? '✓' : ''}
                      </div>
                    )}
                    <div style={{
                      background: ts.bg, height: 52,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {a.cover_photo_url ? (
                        <img
                          src={a.cover_photo_url}
                          alt={a.asset_name || '资产照片'}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <span style={{ fontSize: 24, color: ts.color }}>{ts.icon}</span>
                      )}
                    </div>
                    <div style={{ padding: '6px 8px 8px' }}>
                      <div style={{
                        fontSize: 13, fontWeight: 600, color: '#222',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {a.asset_name}
                      </div>
                      {a.disposal_reported ? (
                        <div style={{ fontSize: 10, color: '#d46b08', marginTop: 1, fontWeight: 600 }}>
                          已申请处置
                        </div>
                      ) : Number(a.disposal_status) === 2 ? (
                        <div style={{ fontSize: 10, color: '#cf1322', marginTop: 1, fontWeight: 600 }}>
                          已处置未完成
                        </div>
                      ) : a.pending_dispose && !isDisposedAssetsMode ? (
                        <div style={{ fontSize: 10, color: '#fa8c16', marginTop: 1 }}>
                          处置审核中
                        </div>
                      ) : a.disposal_status_text && Number(a.disposal_status) === 0 ? (
                        <div style={{ fontSize: 10, color: '#fa8c16', marginTop: 1 }}>
                          处置未上报
                        </div>
                      ) : null}
                      <div
                        title={a.asset_code || ''}
                        style={{
                          fontSize: 11, color: '#666', marginTop: 2,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {formatAssetCodeDisplay(a.asset_code)}
                      </div>
                      <div style={{
                        fontSize: 11, color: '#999', marginTop: 2,
                        display: 'flex', alignItems: 'center', gap: 3,
                        minWidth: 0,
                      }}>
                        <span style={{ fontSize: 10, flexShrink: 0 }}>👤</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {a.user_name || '-'}
                        </span>
                        {!selectedLocationId ? (
                          <>
                            <EnvironmentOutlined style={{ fontSize: 10, flexShrink: 0, marginLeft: 2 }} />
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {a.location_name || '-'}
                            </span>
                          </>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            </div>
          </div>
          </div>
          {loading && (
            <div style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(245,247,250,0.25)',
              zIndex: 2,
            }}
            >
              <Spin />
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div style={{
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            gap: 12, padding: '6px 10px 8px', fontSize: 13, color: '#666',
            flexShrink: 0,
            background: '#f5f7fa',
          }}>
            <div onClick={() => page > 1 && setPage(page - 1)}
              style={{ padding: '4px 12px', borderRadius: 6, background: '#fff', cursor: page > 1 ? 'pointer' : 'not-allowed', color: page > 1 ? '#1677FF' : '#ccc', border: '1px solid #e8e8e8' }}>
              上一页
            </div>
            <span>{page} / {totalPages}</span>
            <div onClick={() => page < totalPages && setPage(page + 1)}
              style={{ padding: '4px 12px', borderRadius: 6, background: '#fff', cursor: page < totalPages ? 'pointer' : 'not-allowed', color: page < totalPages ? '#1677FF' : '#ccc', border: '1px solid #e8e8e8' }}>
              下一页
            </div>
          </div>
        )}
      </div>

      {supportsMultiDispose && selectionMode && (
        <div style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          background: '#fff',
          borderTop: '1px solid #f0f0f0',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.06)',
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: 1200,
        }}
        >
          <div style={{ fontSize: 13, color: '#666' }}>
            已选择 <span style={{ color: '#1677ff', fontWeight: 600 }}>{selectedAssetIds.length}</span> 项
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => {
                setSelectionMode(false);
                setSelectedAssetIds([]);
              }}
              style={{
                border: '1px solid #d9d9d9',
                background: '#fff',
                borderRadius: 6,
                height: 34,
                padding: '0 14px',
                cursor: 'pointer',
                color: '#666',
              }}
            >
              取消
            </button>
            {supportsBatchDirect ? (
              <button
                type="button"
                onClick={handleBatchDirectDispose}
                style={{
                  border: '1px solid #ff4d4f',
                  background: '#ff4d4f',
                  borderRadius: 6,
                  height: 34,
                  padding: '0 14px',
                  cursor: 'pointer',
                  color: '#fff',
                }}
              >
                批量提交未上报
              </button>
            ) : supportsBatchDeleteDisposed ? (
              <button
                type="button"
                onClick={handleBatchDeleteDisposed}
                style={{
                  border: '1px solid #ff4d4f',
                  background: '#ff4d4f',
                  borderRadius: 6,
                  height: 34,
                  padding: '0 14px',
                  cursor: 'pointer',
                  color: '#fff',
                }}
              >
                批量删除
              </button>
            ) : supportsBatchWithdraw ? (
              <button
                type="button"
                onClick={handleBatchWithdrawDispose}
                style={{
                  border: '1px solid #1677ff',
                  background: '#1677ff',
                  borderRadius: 6,
                  height: 34,
                  padding: '0 14px',
                  cursor: 'pointer',
                  color: '#fff',
                }}
              >
                批量申请撤回
              </button>
            ) : supportsBatchApply ? (
              <button
                type="button"
                onClick={handleBatchApplyDispose}
                style={{
                  border: '1px solid #1677ff',
                  background: '#1677ff',
                  borderRadius: 6,
                  height: 34,
                  padding: '0 14px',
                  cursor: 'pointer',
                  color: '#fff',
                }}
              >
                批量申请处置
              </button>
            ) : null
            }
          </div>
        </div>
      )}

      <Modal
        title="高级查询"
        open={searchVisible}
        onCancel={() => setSearchVisible(false)}
        onOk={handleAdvancedSearch}
        okText="查询"
        cancelText="取消"
        destroyOnClose
      >
        <div style={{ fontSize: 12, color: '#999', marginBottom: 12, lineHeight: 1.5 }}>
          「使用人」与「用户管理」为同一张用户表；下拉仅显示姓名。存放地点来自「存放地点管理」。若为空请先在后台维护后再筛选。
        </div>
        <Form form={form} layout="vertical">
          <Form.Item label="资产名称" name="name">
            <Input placeholder="请输入资产名称" allowClear />
          </Form.Item>
          <Form.Item label="资产编号" name="asset_no">
            <Input placeholder="请输入资产编号" allowClear />
          </Form.Item>
          <Form.Item label="清查编号" name="check_no">
            <Input placeholder="请输入清查编号" allowClear />
          </Form.Item>
          <Form.Item label="存放位置" name="location_id">
            <Select
              placeholder={filterOptsLoading ? '加载中…' : '请选择存放位置'}
              allowClear
              showSearch
              loading={filterOptsLoading}
              optionFilterProp="label"
              notFoundContent={filterOptsLoading ? '加载中…' : '暂无地点，请在后台添加存放地点'}
              options={locations.map((l) => ({
                value: Number(l.id),
                label: `${l.location_name || ''}${l.region_name ? `（${l.region_name}）` : ''}`,
              }))}
            />
          </Form.Item>
          <Form.Item label="使用人" name="user_id">
            <Select
              placeholder={filterOptsLoading ? '加载中…' : '请选择使用人'}
              allowClear
              showSearch
              loading={filterOptsLoading}
              optionFilterProp="label"
              notFoundContent={filterOptsLoading ? '加载中…' : '暂无用户，请在「用户管理」中添加'}
              options={assetUsers.map((u) => ({
                value: Number(u.id),
                label: u.username || '',
              }))}
            />
          </Form.Item>
          <Form.Item label="资产状态" name="asset_status">
            <Select
              placeholder="请选择资产状态"
              allowClear
              options={[
                { value: 0, label: '折旧中' },
                { value: 1, label: '已完成折旧' },
              ]}
            />
          </Form.Item>
          <Form.Item label="净值区间">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Form.Item name="net_value_min" noStyle>
                <InputNumber style={{ flex: 1 }} placeholder="最小值" />
              </Form.Item>
              <span>-</span>
              <Form.Item name="net_value_max" noStyle>
                <InputNumber style={{ flex: 1 }} placeholder="最大值" />
              </Form.Item>
            </div>
          </Form.Item>
          <Form.Item label="剩余折旧月数">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Form.Item name="remaining_months_min" noStyle>
                <InputNumber style={{ flex: 1 }} placeholder="最小值" min={0} />
              </Form.Item>
              <span>-</span>
              <Form.Item name="remaining_months_max" noStyle>
                <InputNumber style={{ flex: 1 }} placeholder="最大值" min={0} />
              </Form.Item>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
